import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { eq } from 'tio-ui/utilities';
import {
  PhPiggyBank,
  PhTrendUp,
  PhCurrencyDollar,
  PhGraduationCap,
  PhChartLine,
  PhLightbulb,
  PhArrowRight,
  PhBank,
  PhPercent,
  PhCalendar,
  PhSparkle,
} from 'ember-phosphor-icons';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import PageHero from 'tio-reimagined/components/page-hero';
import StatCard from 'tio-reimagined/components/stat-card';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import IconBadge from 'tio-reimagined/components/icon-badge';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type {
  EmployerContributions,
  FinancialSnapshot,
  Section127Recommendation,
} from 'tio-employee/services/career-pathways-api';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import type { LiveSection127Info } from 'tio-employee/services/reimagined-data';

interface FinanceModel {
  snapshot: FinancialSnapshot;
  recommendation: Section127Recommendation;
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  loansData: {
    loans: LoanAccount[];
    hasLinkedLoans: boolean;
    isDemoMode: boolean;
  };
  section127Info: LiveSection127Info;
}

interface FinanceIndexSignature {
  Args: {
    model: FinanceModel;
  };
}

export default class CareerPathwaysFinanceIndex extends Component<FinanceIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  get snapshot() {
    return this.args.model.snapshot;
  }

  get recommendation() {
    return this.args.model.recommendation;
  }

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  get section127() {
    return this.snapshot.section127;
  }

  get loans() {
    return this.snapshot.studentLoans;
  }

  /**
   * Use live employer contribution data if available, otherwise fall back to snapshot
   */
  get contributions() {
    if (this.args.model.employerContributions.isLiveData) {
      return this.args.model.employerContributions.contributions;
    }
    return this.snapshot.employerContributions;
  }

  get optimization() {
    return this.snapshot.optimizationPotential;
  }

  /**
   * Data source helpers for the DataIndicator component
   */
  get studentLoansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.employerContributions === 'live' ? 'live' : 'placeholder';
  }

  get section127Source(): DataSource {
    // Use live data if Section 127 info was fetched from real plan/contribution data
    if (this.isDemoMode) return 'demo';
    return this.args.model.section127Info.isLiveData ? 'live' : 'placeholder';
  }

  get optimizationSource(): DataSource {
    // AI optimization is always placeholder until fully built
    if (this.isDemoMode) return 'demo';
    return 'placeholder';
  }

  get utilizationPercent() {
    return Math.round((this.section127.yearToDateUsed / this.section127.annualLimit) * 100);
  }

  get loanAllocationPercent() {
    const total = this.section127.loanAllocation + this.section127.tuitionAllocation;
    return total > 0 ? Math.round((this.section127.loanAllocation / total) * 100) : 50;
  }

  get tuitionAllocationPercent() {
    return 100 - this.loanAllocationPercent;
  }

  get formattedPayoffDate() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(new Date(this.loans.estimatedPayoffDate));
  }

  get strategySummary() {
    const strategy = this.section127.strategy;
    if (strategy === 'loan-focused') {
      return 'Prioritizing student loan payments to reduce debt faster';
    }
    if (strategy === 'tuition-focused') {
      return 'Maximizing education investment for career growth';
    }
    return 'Balanced approach between debt reduction and career development';
  }

  get benefitSubtitle() {
    return `Maximize your tax-free ${this.formatCurrency(
      this.section127.annualLimit
    )} education benefit. Our AI analyzes your loans, career goals, and financial situation to optimize your allocation.`;
  }

  formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Hero Banner - Section 127 overview (placeholder until we have real allocation API) }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
        class="mb-6"
      >
        <PageHero
          @icon={{PhPiggyBank}}
          @title="Section 127 Optimizer"
          @subtitle={{this.benefitSubtitle}}
          @theme="emerald"
          role="banner"
        >
          <:stat>
            <div class="text-4xl font-bold">{{this.formatCurrency
                this.optimization.potentialSavings
              }}</div>
            <Text class="text-emerald-100">Potential Annual Savings</Text>
          </:stat>
        </PageHero>
      </DataIndicator>

      {{! Key Metrics Row - Section 127 stats (placeholder until we have real allocation API) }}
      <DataIndicator
        @source={{this.section127Source}}
        @showBadge={{true}}
        @dataSourceId="section-127"
      >
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            @icon={{PhCurrencyDollar}}
            @label="Annual Benefit"
            @value={{this.formatCurrency this.section127.annualLimit}}
            @subtext="Tax-free under Section 127"
            @color="emerald"
          />

          <StatCard
            @icon={{PhChartLine}}
            @label="Used This Year"
            @value={{this.formatCurrency this.section127.yearToDateUsed}}
            @color="blue"
          >
            <:extra>
              <ProgressBar @value={{this.utilizationPercent}} @color="blue" />
              <Text @intent="muted" class="text-xs mt-1">{{this.utilizationPercent}}% utilized</Text>
            </:extra>
          </StatCard>

          <StatCard
            @icon={{PhBank}}
            @label="Remaining"
            @value={{this.formatCurrency this.section127.remaining}}
            @subtext="Available to allocate"
            @color="violet"
            @valueClass="text-emerald-600"
          />

          <StatCard
            @icon={{PhPercent}}
            @label="Tax Savings YTD"
            @value={{this.formatCurrency this.optimization.taxSavingsYtd}}
            @subtext="Based on 25% marginal rate"
            @color="amber"
          />
        </div>
      </DataIndicator>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Current Allocation }}
        <DataIndicator
          @source={{this.section127Source}}
          @showBadge={{true}}
          @dataSourceId="section-127"
          class="lg:col-span-2"
        >
          <div class="bg-white rounded-lg p-6 shadow-sm ring-1 ring-gray-950/[0.04]">
            <div class="flex items-center justify-between mb-4">
              <Heading @level={{2}} class="text-lg font-semibold text-gray-900">Current Allocation</Heading>
              <Chip @intent={{if (eq this.section127.strategy "balanced") "primary" "default"}}>
                {{if
                  (eq this.section127.strategy "loan-focused")
                  "Loan Focused"
                  (if (eq this.section127.strategy "tuition-focused") "Tuition Focused" "Balanced")
                }}
              </Chip>
            </div>

            <Text @intent="muted" class="mb-6">{{this.strategySummary}}</Text>

            {{! Allocation Visualization }}
            <div class="mb-6">
              <div class="flex h-8 rounded-lg overflow-hidden">
                <div
                  class="bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium"
                  style={{htmlSafe (concat "width: " this.loanAllocationPercent "%")}}
                >
                  {{this.loanAllocationPercent}}% Loans
                </div>
                <div
                  class="bg-gradient-to-r from-violet-500 to-violet-600 flex items-center justify-center text-white text-sm font-medium"
                  style={{htmlSafe (concat "width: " this.tuitionAllocationPercent "%")}}
                >
                  {{this.tuitionAllocationPercent}}% Tuition
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              {{! Loan Allocation Card }}
              <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div class="flex items-center gap-2 mb-2">
                  <PhBank @size={{20}} class="text-blue-600" />
                  <Text class="font-medium text-blue-900">Student Loan Payments</Text>
                </div>
                <div class="text-2xl font-bold text-blue-600">{{this.formatCurrency
                    this.section127.loanAllocation
                  }}</div>
                <Text @intent="muted" class="text-sm mt-1">Allocated this year</Text>
              </div>

              {{! Tuition Allocation Card }}
              <div class="bg-violet-50 rounded-lg p-4 border border-violet-200">
                <div class="flex items-center gap-2 mb-2">
                  <PhGraduationCap @size={{20}} class="text-violet-600" />
                  <Text class="font-medium text-violet-900">Tuition Assistance</Text>
                </div>
                <div class="text-2xl font-bold text-violet-600">
                  {{this.formatCurrency this.section127.tuitionAllocation}}
                </div>
                <Text @intent="muted" class="text-sm mt-1">Allocated this year</Text>
              </div>
            </div>

            <div class="mt-6 pt-4 border-t border-gray-200">
              <LinkTo
                @route="authenticated.reimagined.finance.optimizer"
                class="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <PhSparkle @size={{20}} />
                <span>Use AI Optimizer to adjust allocation</span>
                <PhArrowRight @size={{16}} />
              </LinkTo>
            </div>
          </div>
        </DataIndicator>

        {{! Student Loan Summary }}
        <DataIndicator
          @source={{this.studentLoansSource}}
          @showBadge={{true}}
          @dataSourceId="student-loans"
        >
          <div class="bg-white rounded-lg p-6 shadow-sm ring-1 ring-gray-950/[0.04]">
            <div class="flex items-center gap-2 mb-4">
              <PhBank @size={{24}} class="text-gray-600" />
              <Heading @level={{2}} class="text-lg font-semibold text-gray-900">Student Loan Summary</Heading>
            </div>

            <div class="space-y-4">
              <div>
                <Text @intent="muted" class="text-sm">Total Balance</Text>
                <div class="text-2xl font-bold text-gray-900">{{this.formatCurrency
                    this.loans.totalBalance
                  }}</div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Text @intent="muted" class="text-sm">Monthly Payment</Text>
                  <div class="text-lg font-semibold text-gray-900">{{this.formatCurrency
                      this.loans.monthlyPayment
                    }}</div>
                </div>
                <div>
                  <Text @intent="muted" class="text-sm">Interest Rate</Text>
                  <div
                    class="text-lg font-semibold text-gray-900"
                  >{{this.loans.interestRate}}%</div>
                </div>
              </div>

              <div>
                <Text @intent="muted" class="text-sm">Servicer</Text>
                <div class="text-lg font-medium text-gray-900">{{this.loans.servicer}}</div>
              </div>

              <div class="pt-4 border-t border-gray-200">
                <div class="flex items-center gap-2 text-emerald-600 mb-1">
                  <PhCalendar @size={{20}} />
                  <Text class="font-medium">Estimated Payoff</Text>
                </div>
                <div class="text-xl font-bold text-gray-900">{{this.formattedPayoffDate}}</div>
                <Text @intent="muted" class="text-sm">
                  Can accelerate by
                  {{this.optimization.projectedPayoffAcceleration}}
                  with optimization
                </Text>
              </div>
            </div>
          </div>
        </DataIndicator>
      </div>

      {{! AI Recommendation Banner }}
      <DataIndicator
        @source={{this.optimizationSource}}
        @showBadge={{true}}
        @dataSourceId="ai-recommendations"
        class="mt-6"
      >
        <div
          class="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-6 border border-amber-200"
        >
          <div class="flex items-start gap-4">
            <IconBadge @icon={{PhLightbulb}} @color="amber" @size="lg" />
            <div class="flex-1">
              <Heading @level={{3}} class="text-lg font-semibold text-amber-900 mb-2">AI
                Recommendation</Heading>
              <Text class="text-amber-800 mb-4">{{this.recommendation.reasoning}}</Text>

              <div class="flex flex-wrap gap-2">
                {{#each this.recommendation.factors as |factor|}}
                  <Chip @intent={{if (eq factor.impact "high") "warning" "default"}}>
                    {{factor.name}}:
                    {{factor.impact}}
                    impact
                  </Chip>
                {{/each}}
              </div>
            </div>
            <LinkTo @route="authenticated.reimagined.finance.optimizer">
              <Button
                @intent="primary"
                @appearance="outlined"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="View Full Analysis"
              />
            </LinkTo>
          </div>
        </div>
      </DataIndicator>

      {{! Employer Contributions Summary }}
      <DataIndicator
        @source={{this.contributionsSource}}
        @showBadge={{true}}
        @dataSourceId="slr-contributions"
        class="mt-6"
      >
        <div class="bg-white rounded-lg p-6 shadow-sm ring-1 ring-gray-950/[0.04]">
          <div class="flex items-center gap-2 mb-4">
            <PhTrendUp @size={{24}} class="text-emerald-600" />
            <Heading @level={{2}} class="text-lg font-semibold text-gray-900">
              {{this.employerName}}
              Contributions
            </Heading>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="text-center p-4 bg-gray-50 rounded-lg">
              <div class="text-3xl font-bold text-emerald-600">
                {{this.formatCurrency this.contributions.yearToDate}}
              </div>
              <Text @intent="muted">This Year</Text>
            </div>
            <div class="text-center p-4 bg-gray-50 rounded-lg">
              <div class="text-3xl font-bold text-violet-600">{{this.formatCurrency
                  this.contributions.lifetime
                }}</div>
              <Text @intent="muted">Lifetime Total</Text>
            </div>
            <div class="text-center p-4 bg-gray-50 rounded-lg">
              <div class="text-3xl font-bold text-blue-600">
                {{this.formatCurrency this.contributions.projectedAnnual}}
              </div>
              <Text @intent="muted">Projected This Year</Text>
            </div>
          </div>
        </div>
      </DataIndicator>
    </VStack>
  </template>
}
